import { useState, useEffect, useMemo } from "react";
import styles from "./styles.module.css";
import Card from "../Card";
import ConfirmRemoteConsentModal from "../DetailsPage/ConfirmRemoteConsentModal/ConfirmRemoteConsentModal";
import useProfile from "../../hooks/useProfile";
import PersonalInfoForm from "../forms/PersonalInfoForm";
import CocoLanguageForm from "../forms/CocoLanguageForm/index";
import { setLoader } from "../../features/LoaderSlice";
import { useDispatch } from "react-redux";
import useCustomRouting from "../../hooks/useCustomRouting";
import { FormattedMessage, useIntl } from "react-intl";
import useCollect from "../../hooks/useCollect";
import { VAULT, VAULT_ID } from "../../utils/constants";
import MessageBox from "../MessageBox";
import { getSourceSystemId } from "../../utils/utils";
const RemoteConsentForm = ({ values, versionInfo }) => {
  const intl = useIntl();
  const [isSentConsentOpen, setIsSentConsentOpen] = useState(false);
  const { tokenData } = useProfile();
  const dispatch = useDispatch();
  const { redirectToPath } = useCustomRouting();
  const {
    identityCollect,
    handleCollectSuccessMessage,
    handleCollectErrorMessage,
  } = useCollect();
  const [haveRequiredValues, setHaveRequiredValues] = useState(false);
  const [emailTypes, setEmailTypes] = useState([]);

  const [formData, setFormData] = useState({
    first_name: values.FirstName,
    last_name: values.LastName,
    email1: values.EmailInput1 ? decodeURIComponent(values.EmailInput1) : "",
    email2: values.EmailInput2 ? decodeURIComponent(values.EmailInput2) : "",
    default_email1: values.EmailInput1 ? true : false,
    mobile: values.PhoneInput,
    controller_code:
      values.CountryCode?.toLowerCase() + "_" + values.Division?.toLowerCase(),
    customer_scope: "hcp",
    type: getSourceSystemId(values?.SourceSystem),
    value: values.CustomerID ? decodeURIComponent(values.CustomerID) : "",
    language: values.Language,
    contact_email: values.EmailInput1 ? values.EmailInput1 : "",
    title: values.AcademicTitle,
    salutation: values.Salutation,
    bayer_id: values?.BayerID,
    external_provider_id: values?.externalProviderID,
  });
  // variable for restricting edit
  const formDisabled = {
    first_name: true,
    last_name: true,
    email1: true,
    email2: true,
    mobile: true,
    email_type2: !values.EmailInput2,
  };
  const handleCollectCall = (payload) => { 
    dispatch(setLoader({ loading: true, label: "" }));
    identityCollect(payload, true, true, {}, true, true, false, versionInfo)
      .then((res) => {
        handleCollectSuccessMessage(res);
        dispatch(setLoader({ loading: false, label: "" }));
        const urlParams = new URLSearchParams(window.location.search);
        redirectToPath(`/coco/?${urlParams}`);
      })
      .catch((error) => {
        handleCollectErrorMessage(error);
        dispatch(setLoader({ loading: false, label: "" }));
      });
  };

  const handleOpenSample = () => setIsSentConsentOpen(true);

  const handleChange = (e) => {
    if (["default_email1", "default_email2"]?.includes(e.target.name)) {
      const isMail1 = e.target.name == "default_email1";
      setFormData((prev) => ({
        ...prev,
        default_email1: !!isMail1,
        default_email2: !isMail1,
        hasSubmitted: false,
      }));
    } else
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
        hasSubmitted: false,
      }));
  };

  const handleSubmitCollect = () => {
    setFormData((prev) => ({ ...prev, hasSubmitted: true }));
    if (haveRequiredValues) {
      dispatch(setLoader({ loading: true, label: "Saving..." }));
      const sourceFields = [];
      const sub_identities = [];
      const aliases = [];
      if (formData.email1) {
        const isPersonal = formData?.email_type1 == "Account.PersonEmail";
        sourceFields.push({
          field: "Account.PersonEmail",
          value: formData.email1,
        });
        if (isPersonal)
          sub_identities.push({
            value: formData.email1,
            type: "email",
          });
        else
          aliases.push({
            value: formData.email1,
            type: "email",
          });
      }
      if (formData.email2) {
        const isPersonal = formData?.email_type2 == "Account.PersonEmail";
        sourceFields.push({
          field: "BPH_Email_2__c",
          value: formData.email2,
        });
        if (isPersonal)
          sub_identities.push({
            value: formData.email2,
            type: "email",
          });
        else
          aliases.push({
            value: formData.email2,
            type: "email",
          });
      }
      const payload = {
        controller_code: formData.controller_code,
        customer_scope: formData.customer_scope,
        type: formData.type,
        value: formData.value,
        language: formData.language,
        title: formData.title,
        salutation: formData.salutation,
        last_name: formData.last_name,
        first_name: formData.first_name,
        loggedin_email: tokenData?.upn,
        remote_collector_email: tokenData?.upn,
        contact_email: formData?.default_email1
          ? formData.email1
          : formData.email2,

        collection_channel: "Truman UI",
        last_update_channel: "Truman UI",
        external_provider_id: formData?.external_provider_id,
      };
      if (formData?.type == "veeva" && sourceFields?.length)
        payload.source_fields = { veeva: sourceFields };
      else if (formData?.type == VAULT_ID && sourceFields?.length)
        payload.source_fields = {
          [VAULT]: sourceFields?.map((fields) => {
            if (fields.field === "Account.PersonEmail") {
              return { ...fields, field: "account__v.email_cda__v" };
            } else if (fields.field === "BPH_Email_2__c") {
              return { ...fields, field: "account__v.bph_email_2__c" };
            } else {
              return fields;
            }
          }),
        };
      if (aliases?.length) payload.aliases = aliases;
      if (sub_identities?.length) payload.sub_identities = sub_identities;
      if (formData?.bayer_id) payload.bayer_id = formData?.bayer_id;
      handleCollectCall({ ...payload });
    }
  };

  const getMinRequiredFields = () => {
    if (Number(versionInfo?.requiredAnyFields))
      return Number(versionInfo?.requiredAnyFields);
    else {
      if (versionInfo?.requiredFields)
        return versionInfo?.requiredFields?.split(",")?.length;
      else if (values?.EmailInput1 || values?.EmailInput2) return 1;
      else return 0;
    }
  };
  const requiredAnyFields = useMemo(
    () => Number(versionInfo?.requiredAnyFields),
    [versionInfo],
  );
  const isEmail1Required = useMemo(
    () => versionInfo?.requiredFields?.includes("Account.PersonEmail"),
    [versionInfo],
  );
  const isEmail2Required = useMemo(
    () => versionInfo?.requiredFields?.includes("BPH_Email_2__c"),
    [versionInfo],
  );

  const addEmailValidations = (validation, emailField, emailTypeField) => {
    validation[emailField] = {
      required: true,
      message: versionInfo?.requiredValidationError,
    };
    validation[emailTypeField] = {
      required: true,
      message: versionInfo?.requiredValidationError,
    };
  };

  const getValidationError = () => {
    const validation = {
      first_name: {
        required: true,
        message: versionInfo?.requiredValidationError,
      },
      last_name: {
        required: true,
        message: versionInfo?.requiredValidationError,
      },
    };
    const minVal = getMinRequiredFields();
    if (minVal) {
      if ((!requiredAnyFields && isEmail1Required) || values?.EmailInput1) {
        addEmailValidations(validation, "email1", "email_type1");
      }
      if ((!requiredAnyFields && isEmail2Required) || values?.EmailInput2) {
        addEmailValidations(validation, "email2", "email_type2");
      }
    }
    return validation;
  };

  useEffect(() => {
    const isValid = () => {
      if (!(formData?.first_name && formData?.last_name)) {
        return false;
      }
      if (!(formData?.email1 || formData?.email2)) {
        return false;
      }

      const minVal = getMinRequiredFields();
      if (minVal) {
        if (
          minVal > 1 &&
          !(
            formData?.email1 &&
            formData?.email2 
          )
        ) {
          return false;
        } else if (
          versionInfo?.requiredFields?.split(",")?.length == 1 &&
          ((isEmail1Required && !formData?.email1) ||
            (isEmail2Required && !formData?.email2))
        ) {
          return false;
        }
      }
      return true;
    };
    setHaveRequiredValues(isValid());
  }, [formData, versionInfo]);
  useEffect(() => {
    const personal = {
      value: "Account.PersonEmail",
      label: intl.formatMessage({
        id: "label.personal",
        defaultMessage: "Personal",
      }),
    };
    const shared = {
      value: "BPH_Email_2__c",
      label: intl.formatMessage({
        id: "label.shared",
        defaultMessage: "Shared",
      }),
    };
    const types = [];
    if (versionInfo?.emailTypes) {
      if (versionInfo?.emailTypes?.includes("personal")) types.push(personal);
      if (versionInfo?.emailTypes?.includes("shared")) types.push(shared);
    } else {
      types?.push(personal);
      types?.push(shared);
    }
    setEmailTypes(types);
  }, [versionInfo]);

  useEffect(() => {
    if (emailTypes?.length) {
      const emailTypeValue = emailTypes[0]?.value;

      setFormData((prev) => ({
        ...prev,
        ...(formData?.email1 &&
          !formData?.email_type1 && { email_type1: emailTypeValue }),
        ...(formData?.email2 &&
          !formData?.email_type2 && { email_type2: emailTypeValue }),
      }));
    }
  }, [emailTypes]);

  const hasEmail = Boolean(formData.email1 || formData.email2);
  return (
    <div className={styles["form-container"]}>
      <h1 className={styles["consent-title"]}>
        <FormattedMessage
          id="coco.option.remote-consent"
          defaultMessage="Remote Consent"
        />
      </h1>
      {!hasEmail && (
        <div className={styles["container-no-email-validation"]}>
          <MessageBox
            label={intl.formatMessage({
              id: "coco.no-email-validation",
              defaultMessage:
                "For Remote Consent Collect, an email address is required. Please add an email address in the CRM system and try again.",
            })}
            state="error"
          />
        </div>
      )}
      <Card className={styles["form-card"]}>
        <p className={styles["form-title"]}>
          <FormattedMessage
            id="coco.rc-form.personal-data"
            defaultMessage="Personal Data"
          />
        </p>
        <PersonalInfoForm
          formDisabled={formDisabled}
          formData={formData}
          handleChange={handleChange}
          versionInfo={versionInfo}
          emailTypes={emailTypes}
          queryValues={values}
          getValidationError={getValidationError}
        />
      </Card>
      <Card className={styles["form-card"]}>
        <p className={styles["form-title"]}>
          <FormattedMessage id="label.consent" defaultMessage="Consent" />
        </p>
        <CocoLanguageForm
          formData={formData}
          handleChange={handleChange}
          handleOpenSample={handleOpenSample}
          versionInfo={versionInfo}
          setFormData={setFormData}
        />
      </Card>
      <button
        className={`btn-primary ${styles["submit-button"]}`}
        disabled={!hasEmail || formData?.language === ""}
        onClick={handleSubmitCollect}
      >
        <FormattedMessage
          id="button.collect-consent"
          defaultMessage="Collect Consent"
        />
      </button>
      <ConfirmRemoteConsentModal
        isSentConsentOpen={isSentConsentOpen}
        setIsSentConsentOpen={setIsSentConsentOpen}
        user={{
          controllerCode: formData.controller_code,
          customerScope: formData.customer_scope,
          type: formData.type,
          value: formData.value,
          language: formData.language,
          contactEmail: formData.contact_email,
        }}
        handleSubmitCollect={handleSubmitCollect}
      />
    </div>
  );
};

export default RemoteConsentForm;